import Iconfont from '@/components/iconfont/iconfont'
import { Column, Row } from '@/components/layout/layout'
import PopoverConfirm from '@/components/popover-confirm/popover-confirm'
import { isExist } from '@/utils'
import useBemClass from '@/utils/hooks/useBEMClass'
import { useMount, useRequest, useSelections } from 'ahooks'
import { observer } from 'mobx-react'
import React, { useMemo } from 'react'
import { goodsModel } from '../../goods-storage/goods-library/modules/edit-goods/modules/model'
import classnames from 'classnames'
import './group-popover.scss'
import { $post } from '@/api'
import { Group } from '@/model/types/goods'

interface GroupPopoverMutipleProps {
    visible?: boolean
    onVisibleChange?: (v: boolean) => void
    hasMallGroup?: boolean
    onCancel?: () => void
    onSubmit: (groupId: (number | string | undefined)[], secondId: (number | string)[], name: string[]) => void
}

interface GroupPopoverList extends Group {
    is_shelf: boolean
}

const GroupPopoverMutiple: React.FC<GroupPopoverMutipleProps> = observer(props => {
    const cls = useBemClass('group-popover')

    useRequest<{
        independent_shelf_opened: boolean
    }>(
        () =>
            $post({
                url: '/mall/getshelfconfig',
            }),
        {
            manual: !props.hasMallGroup,
            onSuccess: res => {
                if (res.independent_shelf_opened) {
                    mallGroupReq.run()
                }
            },
        },
    )

    const mallGroupReq = useRequest<Group[]>(
        () =>
            $post({
                url: '/mall/getmallgoodsgroup',
            }),
        {
            manual: true,
        },
    )

    const groupList = useMemo<GroupPopoverList[]>(() => {
        const res = goodsModel.goodsGroup.map(v => ({
            ...v,
            is_shelf: false,
        }))

        if (mallGroupReq.data?.length) {
            return [
                ...res,
                ...mallGroupReq.data.map(v => ({
                    ...v,
                    is_shelf: true,
                })),
            ]
        }

        return res
    }, [mallGroupReq.data])

    const groupIds = useMemo(() => {
        return groupList.map(g => (g.secondary_group.length ? 0 : g.is_shelf ? `_${g.id}` : g.id)).filter(Boolean)
    }, [groupList])

    const secondGroupIds = useMemo(() => {
        return groupList.reduce<(number | string)[]>((pre, next) => {
            if (next.secondary_group.length) {
                const ids = next.secondary_group.map(s => (next.is_shelf ? `_${s.id}` : s.id))

                return [...pre, ...ids]
            }

            return pre
        }, [])
    }, [groupList])

    const allGroupName = useMemo(() => {
        const names = groupList.reduce<string[]>((pre, next) => {
            if (next.secondary_group.length) {
                const ids = next.secondary_group.map(s => `${next.name}/${s.name}`)

                return [...pre, ...ids]
            } else {
                return [...pre, next.name]
            }
        }, [])

        return names
    }, [groupList])

    const groupIdSelection = useSelections<number | string | undefined>(groupIds, [])
    const secondGroupIdSelection = useSelections<number | string>(secondGroupIds, [])
    const groupNameSelection = useSelections<string>(allGroupName, [])

    useMount(() => {
        goodsModel.initGoodsGroupInfo()
    })

    return (
        <PopoverConfirm
            arrowPointAtCenter
            trigger="click"
            types="success"
            {...props}
            content={
                <Column data-position-relative>
                    <div className={cls('title')} data-center>
                        选择分组
                    </div>
                    <Column className={cls('container')}>
                        {groupList.map(group => (
                            <Column key={group.id} className={cls('line')}>
                                <div
                                    className={cls('group')}
                                    data-justify-content-space-between
                                    onClick={() => {
                                        groupIdSelection.toggle(
                                            group.is_shelf ? `_${Number(group.id)}` : Number(group.id),
                                        )
                                        groupNameSelection.toggle(group.name)
                                    }}
                                >
                                    <Row data-align-items-center>
                                        {!isExist(group.secondary_group) && (
                                            <Iconfont
                                                name={
                                                    groupIdSelection.isSelected(
                                                        group.is_shelf ? `_${Number(group.id)}` : Number(group.id),
                                                    )
                                                        ? 'icon3duoxuanerji-20'
                                                        : 'icon3duoxuanguan-20'
                                                }
                                                size={20}
                                            />
                                        )}
                                        <div
                                            className={classnames(cls('tag'), {
                                                [cls('default')]: group.is_default,
                                            })}
                                            style={{
                                                marginLeft: !isExist(group.secondary_group) ? '16px' : '36px',
                                            }}
                                        >
                                            {group.name.charAt(0)}
                                        </div>
                                        <div className={cls('txt')}>{group.name}</div>
                                    </Row>
                                    {!props.hasMallGroup ? (
                                        <Row data-align-items-center>
                                            <Iconfont name="icon2shangjiashouyintai" size={20} />
                                            <Iconfont name="icon2shangjiaweishangcheng" size={20} />
                                        </Row>
                                    ) : group.is_shelf ? (
                                        <Iconfont name="icon2shangjiaweishangcheng" size={20} />
                                    ) : (
                                        <Iconfont name="icon2shangjiashouyintai" size={20} />
                                    )}
                                </div>
                                {isExist(group.secondary_group) &&
                                    group.secondary_group.map(second => (
                                        <div key={second.id}>
                                            <div
                                                className={cls('group')}
                                                onClick={() => {
                                                    secondGroupIdSelection.toggle(
                                                        group.is_shelf ? `_${second.id}` : second.id,
                                                    )
                                                    groupNameSelection.toggle(`${group.name}/${second.name}`)
                                                }}
                                            >
                                                <Iconfont
                                                    name={
                                                        secondGroupIdSelection.isSelected(
                                                            group.is_shelf ? `_${second.id}` : second.id,
                                                        )
                                                            ? 'icon3duoxuanerji-20'
                                                            : 'icon3duoxuanguan-20'
                                                    }
                                                    size={20}
                                                />
                                                <div
                                                    className={cls('txt')}
                                                    style={{
                                                        marginLeft: '64px',
                                                    }}
                                                >
                                                    {second.name}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                            </Column>
                        ))}
                    </Column>

                    <Row
                        data-align-items-center
                        data-cursor-pointer
                        className={cls('all')}
                        onClick={() => {
                            groupNameSelection.toggleAll()
                            groupIdSelection.toggleAll()
                            secondGroupIdSelection.toggleAll()
                        }}
                    >
                        <Iconfont
                            name={
                                groupNameSelection.allSelected
                                    ? 'icon3duoxuanquanxuan-20'
                                    : groupNameSelection.partiallySelected
                                    ? 'icon3duoxuanweiquanxuan-20'
                                    : 'icon3duoxuanguan-20'
                            }
                            size={20}
                        />
                        <div data-color-000 data-font-size-14 data-margin-left-6>
                            全选
                        </div>
                    </Row>
                </Column>
            }
            cancelText="重置"
            onCancel={() => {
                groupIdSelection.unSelectAll()
                secondGroupIdSelection.unSelectAll()
                groupNameSelection.unSelectAll()
            }}
            disabled={!groupNameSelection.selected.length}
            onConfirm={() => {
                props.onSubmit(groupIdSelection.selected, secondGroupIdSelection.selected, groupNameSelection.selected)
            }}
        >
            {props.children}
        </PopoverConfirm>
    )
})

GroupPopoverMutiple.defaultProps = {
    hasMallGroup: true,
}

export default GroupPopoverMutiple
